import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ThemeService } from '../../../core/services/theme.service';
import { markFormGroupTouched } from '../utils/form-validation.utils';

@Component({
    standalone: true,
    selector: 'app-growth-rate-calculator',
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule
    ],
    templateUrl: './growth-rate-calculator.component.html',
    styleUrl: './growth-rate-calculator.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrowthRateCalculatorComponent implements OnInit {

  calculatorForm: FormGroup;
  cagrResult: number | null = null;
  totalGrowth: number = 0;
  errorMessage: string = '';
  isCalculating: boolean = false;

  // Theme service - centralized theme management
  themeService = inject(ThemeService);

  constructor(private fb: FormBuilder) {
    this.calculatorForm = this.fb.group({
      endingValue: [null, [Validators.required, Validators.min(0.01)]],
      beginningValue: [null, [Validators.required, Validators.min(0.01)]],
      periods: [null, [Validators.required, Validators.min(0.01)]],
      startYear: [null],
      endYear: [null]
    });
  }

  ngOnInit(): void {
    // Set default current year
    const currentYear = new Date().getFullYear();
    this.calculatorForm.patchValue({
      endYear: currentYear
    });
  }

  /**
   * Update periods based on start and end years
   */
  updatePeriodsFromYears(): void {
    const startYear = this.calculatorForm.get('startYear')?.value;
    const endYear = this.calculatorForm.get('endYear')?.value;

    if (startYear && endYear && endYear > startYear) {
      const periods = endYear - startYear;
      this.calculatorForm.patchValue({
        periods: periods
      });
    }
  }

  /**
   * Calculate CAGR using the formula: (Ending Value / Beginning Value)^(1/Number of Periods) - 1
   */
  calculateCAGR(): void {
    if (this.calculatorForm.invalid) {
      markFormGroupTouched(this.calculatorForm);
      return;
    }

    this.isCalculating = true;
    this.errorMessage = '';

    try {
      const endingValue = this.calculatorForm.get('endingValue')?.value;
      const beginningValue = this.calculatorForm.get('beginningValue')?.value;
      const periods = this.calculatorForm.get('periods')?.value;

      // Validation
      if (beginningValue <= 0) {
        throw new Error('Beginning value must be greater than zero');
      }

      if (endingValue <= 0) {
        throw new Error('Ending value must be greater than zero');
      }

      if (periods <= 0) {
        throw new Error('Number of periods must be greater than zero');
      }

      // Calculate CAGR
      const growthRatio = endingValue / beginningValue;
      const cagr = (Math.pow(growthRatio, 1 / periods) - 1) * 100;

      // Calculate total growth percentage
      this.totalGrowth = ((endingValue - beginningValue) / beginningValue) * 100;

      this.cagrResult = cagr;

    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'An error occurred during calculation';
      this.cagrResult = null;
    } finally {
      this.isCalculating = false;
    }
  }

  /**
   * Clear the form and reset results
   */
  clearForm(): void {
    this.calculatorForm.reset();
    this.cagrResult = null;
    this.totalGrowth = 0;
    this.errorMessage = '';

    // Reset default current year
    const currentYear = new Date().getFullYear();
    this.calculatorForm.patchValue({
      endYear: currentYear
    });
  }

  /**
   * Get interpretation of the CAGR result
   */
  getInterpretation(): string {
    if (this.cagrResult === null) return '';

    const cagr = this.cagrResult;

    if (cagr < -10) {
      return 'Significant decline - This represents a substantial annual decrease. Consider investigating the underlying causes.';
    } else if (cagr < 0) {
      return 'Negative growth - The value has declined over this period. This may indicate challenges or market conditions.';
    } else if (cagr < 5) {
      return 'Low growth - Modest growth rate. This may be acceptable for stable, mature businesses or conservative investments.';
    } else if (cagr < 10) {
      return 'Moderate growth - Solid growth rate that indicates healthy expansion. This is often considered good for established companies.';
    } else if (cagr < 20) {
      return 'Strong growth - Excellent growth rate that suggests robust business performance and expansion.';
    } else if (cagr < 30) {
      return 'Very strong growth - Outstanding growth rate. This level of growth is often seen in high-growth companies or emerging markets.';
    } else {
      return 'Exceptional growth - Extremely high growth rate. While impressive, ensure this level of growth is sustainable long-term.';
    }
  }

  /**
   * Get CSS class for interpretation styling
   */
  getInterpretationClass(): string {
    if (this.cagrResult === null) return '';

    const cagr = this.cagrResult;

    if (cagr < -10) {
      return 'bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 text-red-800 dark:text-red-300';
    } else if (cagr < 0) {
      return 'bg-orange-100 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-800 text-orange-800 dark:text-orange-300';
    } else if (cagr < 5) {
      return 'bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300';
    } else if (cagr < 15) {
      return 'bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-800 text-green-800 dark:text-green-300';
    } else {
      return 'bg-blue-100 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-800 text-blue-800 dark:text-blue-300';
    }
  }

  /**
   * Get icon for interpretation
   */
  getInterpretationIcon(): string {
    if (this.cagrResult === null) return '';

    const cagr = this.cagrResult;

    if (cagr < -10) {
      return 'pi pi-arrow-down text-red-600';
    } else if (cagr < 0) {
      return 'pi pi-minus text-orange-600';
    } else if (cagr < 5) {
      return 'pi pi-arrow-up text-yellow-600';
    } else if (cagr < 15) {
      return 'pi pi-trending-up text-green-600';
    } else {
      return 'pi pi-star text-blue-600';
    }
  }

  /**
   * Calculate future value projection based on current CAGR
   */
  getFutureValue(years: number): number {
    if (this.cagrResult === null) return 0;

    const currentValue = this.calculatorForm.get('endingValue')?.value || 0;
    const growthRate = this.cagrResult / 100;

    return currentValue * Math.pow(1 + growthRate, years);
  }
}

